/**
 * MilestoneBanner — celebratory banner with confetti effect for milestone achievements.
 * Pure RN implementation (no native confetti library).
 */

import { useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, Animated, Easing, TouchableOpacity } from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import type { MilestoneDef } from '@/lib/milestones';

interface MilestoneBannerProps {
  milestone: MilestoneDef;
  onDismiss: () => void;
}

const CONFETTI_COLORS = ['#FFD700', '#FF6B6B', '#4CAF50', '#2196F3', '#FF9800', '#E040FB'];
const NUM_CONFETTI = 20;

function ConfettiPiece({ delay, color, startX }: { delay: number; color: string; startX: number }) {
  const anim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.loop(
      Animated.sequence([
        Animated.delay(delay),
        Animated.timing(anim, {
          toValue: 1,
          duration: 2000,
          easing: Easing.out(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    ).start();
  }, []);

  return (
    <Animated.View
      style={{
        position: 'absolute',
        left: startX,
        top: 0,
        width: 6,
        height: 10,
        borderRadius: 2,
        backgroundColor: color,
        opacity: anim.interpolate({ inputRange: [0, 0.2, 1], outputRange: [0, 1, 0] }),
        transform: [
          { translateY: anim.interpolate({ inputRange: [0, 1], outputRange: [-10, 80] }) },
          { rotate: anim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '360deg'] }) },
          { scale: anim.interpolate({ inputRange: [0, 0.5, 1], outputRange: [0.5, 1, 0.3] }) },
        ],
      }}
    />
  );
}

export default function MilestoneBanner({ milestone, onDismiss }: MilestoneBannerProps) {
  const { theme } = useTheme();
  const c = theme.colors;
  const slideAnim = useRef(new Animated.Value(-120)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.parallel([
      Animated.spring(slideAnim, { toValue: 0, useNativeDriver: true, speed: 8 }),
      Animated.timing(opacityAnim, { toValue: 1, duration: 300, useNativeDriver: true }),
    ]).start();

    // Auto-dismiss after 5 seconds
    const timer = setTimeout(() => {
      Animated.parallel([
        Animated.timing(slideAnim, { toValue: -120, duration: 300, useNativeDriver: true }),
        Animated.timing(opacityAnim, { toValue: 0, duration: 300, useNativeDriver: true }),
      ]).start(() => onDismiss());
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  const confettiPieces = Array.from({ length: NUM_CONFETTI }, (_, i) => ({
    key: i,
    delay: Math.random() * 500,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    startX: Math.random() * 300,
  }));

  return (
    <Animated.View
      style={[
        styles.banner,
        {
          backgroundColor: c.accent,
          transform: [{ translateY: slideAnim }],
          opacity: opacityAnim,
        },
      ]}>
      {/* Confetti layer */}
      <View style={styles.confettiContainer} pointerEvents="none">
        {confettiPieces.map((p) => (
          <ConfettiPiece key={p.key} delay={p.delay} color={p.color} startX={p.startX} />
        ))}
      </View>

      <TouchableOpacity style={styles.content} onPress={onDismiss} activeOpacity={0.8}>
        <Text style={styles.emoji}>{milestone.emoji}</Text>
        <View style={styles.textCol}>
          <Text style={[styles.title, { color: c.accentForeground }]}>{milestone.label}</Text>
          <Text style={[styles.description, { color: c.accentForeground, opacity: 0.8 }]}>{milestone.description}</Text>
        </View>
      </TouchableOpacity>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  banner: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    borderBottomLeftRadius: 16,
    borderBottomRightRadius: 16,
    paddingTop: 50,
    paddingBottom: 16,
    paddingHorizontal: 20,
    overflow: 'hidden',
  },
  confettiContainer: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
  },
  emoji: {
    fontSize: 36,
  },
  textCol: {
    flex: 1,
  },
  title: {
    fontSize: 18,
    fontWeight: '900',
  },
  description: {
    fontSize: 13,
    fontWeight: '600',
    marginTop: 2,
  },
});
